/**
 * Pine Script template for dynamic support/resistance and Fibonacci level generation
 * Placeholders are marked with {{VARIABLE_NAME}} for easy replacement
 */
export const PINE_SCRIPT_TEMPLATE = `//@version=5
indicator("Support/Resistance & Fibonacci Levels", overlay=true, max_lines_count=500, max_labels_count=500, max_boxes_count=100)

// Input parameters for customization
show_sr = input.bool(true, "Show Support/Resistance Levels", group="Display Options")
show_fib_ret = input.bool(true, "Show Fibonacci Retracements", group="Display Options")
show_fib_ext = input.bool(true, "Show Fibonacci Extensions", group="Display Options")
line_width = input.int(2, "Line Width", minval=1, maxval=5, group="Display Options")

// Fibonacci swing points
swing_high = input.float({{SWING_HIGH}}, "Swing High", step=0.25, group="Fibonacci Settings")
swing_low = input.float({{SWING_LOW}}, "Swing Low", step=0.25, group="Fibonacci Settings")

// Resistance Levels
major_resistance = input.float({{MAJOR_RESISTANCE}}, "Major Resistance", step=0.25, group="Resistance Levels")
resistance_recovery_high = input.float({{RESISTANCE_RECOVERY_HIGH}}, "Recovery High", step=0.25, group="Resistance Levels")
resistance_recovery_low = input.float({{RESISTANCE_RECOVERY_LOW}}, "Recovery Low", step=0.25, group="Resistance Levels")
minor_resistance = input.float({{MINOR_RESISTANCE}}, "Minor Resistance", step=0.25, group="Resistance Levels")
resistance_level_5 = input.float({{RESISTANCE_LEVEL_5}}, "Resistance Level 5", step=0.25, group="Resistance Levels")
resistance_level_6 = input.float({{RESISTANCE_LEVEL_6}}, "Resistance Level 6", step=0.25, group="Resistance Levels")

// Support Levels
immediate_support = input.float({{IMMEDIATE_SUPPORT}}, "Immediate Support", step=0.25, group="Support Levels")
major_support_1 = input.float({{MAJOR_SUPPORT_1}}, "Major Support 1", step=0.25, group="Support Levels")
major_support_2 = input.float({{MAJOR_SUPPORT_2}}, "Major Support 2", step=0.25, group="Support Levels")
support_level_4 = input.float({{SUPPORT_LEVEL_4}}, "Support Level 4", step=0.25, group="Support Levels")

// AI-Specified Fibonacci Retracement Levels (from AI analysis)
fib_236 = input.float({{FIB_236}}, "Fib 23.6% Retracement", step=0.01, group="AI Fibonacci Levels")
fib_382 = input.float({{FIB_382}}, "Fib 38.2% Retracement", step=0.01, group="AI Fibonacci Levels")
fib_500 = input.float({{FIB_500}}, "Fib 50.0% Retracement", step=0.01, group="AI Fibonacci Levels")
fib_618 = input.float({{FIB_618}}, "Fib 61.8% Retracement", step=0.01, group="AI Fibonacci Levels")
fib_786 = input.float({{FIB_786}}, "Fib 78.6% Retracement", step=0.01, group="AI Fibonacci Levels")

// AI-Specified Fibonacci Extension Levels (downside targets from AI analysis)
fib_ext_1272 = input.float({{FIB_EXT_1272}}, "Fib 127.2% Extension", step=0.01, group="AI Fibonacci Extensions")
fib_ext_1618 = input.float({{FIB_EXT_1618}}, "Fib 161.8% Extension", step=0.01, group="AI Fibonacci Extensions")

// Calculate swing range for reference
fib_range = swing_high - swing_low

// Plot Resistance Levels with horizontal lines
hline(major_resistance, "Major Resistance", color=color.red, linestyle=hline.style_solid, linewidth=2)
hline(resistance_recovery_high, "Recovery High", color=color.orange, linestyle=hline.style_solid, linewidth=2)
hline(resistance_recovery_low, "Recovery Low", color=color.orange, linestyle=hline.style_dashed, linewidth=1)
hline(minor_resistance, "Minor Resistance", color=color.yellow, linestyle=hline.style_dotted, linewidth=1)
hline(resistance_level_5, "Resistance Level 5", color=color.gray, linestyle=hline.style_dotted, linewidth=1)
hline(resistance_level_6, "Resistance Level 6", color=color.gray, linestyle=hline.style_dotted, linewidth=1)

// Plot Support Levels with horizontal lines
hline(immediate_support, "Immediate Support", color=color.lime, linestyle=hline.style_solid, linewidth=2)
hline(major_support_1, "Major Support 1", color=color.green, linestyle=hline.style_solid, linewidth=2)
hline(major_support_2, "Major Support 2", color=color.green, linestyle=hline.style_dashed, linewidth=1)
hline(support_level_4, "Support Level 4", color=color.green, linestyle=hline.style_dotted, linewidth=1)

// Plot Fibonacci Retracement Levels with horizontal lines
hline(fib_236, "Fib 23.6%", color=color.purple, linestyle=hline.style_dotted, linewidth=1)
hline(fib_382, "Fib 38.2% (Critical Support)", color=color.blue, linestyle=hline.style_solid, linewidth=2)
hline(fib_500, "Fib 50%", color=color.yellow, linestyle=hline.style_solid, linewidth=2)
hline(fib_618, "Fib 61.8% (Golden Ratio)", color=color.fuchsia, linestyle=hline.style_solid, linewidth=2)
hline(fib_786, "Fib 78.6%", color=color.purple, linestyle=hline.style_dotted, linewidth=1)

// Plot Fibonacci Extension Levels (Downside Targets)
hline(fib_ext_1272, "Fib Ext 127.2%", color=color.maroon, linestyle=hline.style_dashed, linewidth=1)
hline(fib_ext_1618, "Fib Ext 161.8%", color=color.red, linestyle=hline.style_dashed, linewidth=1)

// Plot swing high and low reference points
hline(swing_high, "Swing High", color=color.white, linestyle=hline.style_solid, linewidth=2)
hline(swing_low, "Swing Low", color=color.white, linestyle=hline.style_solid, linewidth=2)

// Create labels on the last bar for key levels
if barstate.islast
    label.new(bar_index, major_resistance, "Major Resistance (" + str.tostring(major_resistance, "#.##") + ")", 
              style=label.style_label_left, color=color.red, textcolor=color.white, size=size.small)
    label.new(bar_index, resistance_recovery_high, "Recovery High (" + str.tostring(resistance_recovery_high, "#.##") + ")", 
              style=label.style_label_left, color=color.orange, textcolor=color.white, size=size.small)
    label.new(bar_index, immediate_support, "Immediate Support (" + str.tostring(immediate_support, "#.##") + ")", 
              style=label.style_label_left, color=color.lime, textcolor=color.black, size=size.small)
    label.new(bar_index, major_support_1, "Major Support (" + str.tostring(major_support_1, "#.##") + ")", 
              style=label.style_label_left, color=color.green, textcolor=color.white, size=size.small)
    
    label.new(bar_index, fib_382, "Fib 38.2% (" + str.tostring(fib_382, "#.##") + ")", 
              style=label.style_label_left, color=color.blue, textcolor=color.white, size=size.small)
    label.new(bar_index, fib_500, "Fib 50% (" + str.tostring(fib_500, "#.##") + ")", 
              style=label.style_label_left, color=color.yellow, textcolor=color.black, size=size.small)
    label.new(bar_index, fib_618, "Fib 61.8% (" + str.tostring(fib_618, "#.##") + ")", 
              style=label.style_label_left, color=color.fuchsia, textcolor=color.white, size=size.small)

// Enhanced info table with current market context
if barstate.islast
    var table infoTable = table.new(position.top_right, 2, 11, bgcolor=color.new(color.black, 20), border_width=1)
    table.cell(infoTable, 0, 0, "Current Price", text_color=color.white, bgcolor=color.new(color.gray, 50))
    table.cell(infoTable, 1, 0, str.tostring(close, "#.##"), text_color=color.yellow, bgcolor=color.new(color.gray, 50))
    
    table.cell(infoTable, 0, 1, "Swing Range", text_color=color.white)
    table.cell(infoTable, 1, 1, str.tostring(fib_range, "#.##"), text_color=color.white)
    
    table.cell(infoTable, 0, 2, "Current Fib Level", text_color=color.white)
    current_fib_pct = (close - swing_low) / fib_range * 100
    table.cell(infoTable, 1, 2, str.tostring(current_fib_pct, "#.#") + "%", text_color=color.yellow)
    
    table.cell(infoTable, 0, 3, "Distance to 38.2%", text_color=color.white)
    fib_382_distance = close - fib_382
    fib_382_color = fib_382_distance > 0 ? color.green : color.red
    table.cell(infoTable, 1, 3, str.tostring(fib_382_distance, "#.##"), text_color=fib_382_color)
    
    table.cell(infoTable, 0, 4, "Distance to 61.8%", text_color=color.white)
    fib_618_distance = close - fib_618
    fib_618_color = fib_618_distance > 0 ? color.green : color.red
    table.cell(infoTable, 1, 4, str.tostring(fib_618_distance, "#.##"), text_color=fib_618_color)
    
    table.cell(infoTable, 0, 5, "To Recovery High", text_color=color.white)
    recovery_distance = resistance_recovery_high - close
    table.cell(infoTable, 1, 5, str.tostring(recovery_distance, "#.##"), text_color=color.orange)
    
    table.cell(infoTable, 0, 6, "Market Phase", text_color=color.white)
    market_phase = close > fib_618 ? "Recovery" : close > fib_500 ? "Consolidation" : close > fib_382 ? "Weak Bounce" : "Bearish"
    phase_color = close > fib_618 ? color.green : close > fib_382 ? color.yellow : color.red
    table.cell(infoTable, 1, 6, market_phase, text_color=phase_color)

// Alert conditions for key levels based on the analysis
alertcondition(ta.crossover(close, fib_618), title="Break Above 61.8% Fib", message="Price broke above 61.8% Fib - Recovery strength confirmed")
alertcondition(ta.crossunder(close, fib_382), title="Break Below 38.2% Fib", message="Price broke below 38.2% Fib - Weakness confirmed")
alertcondition(ta.crossover(close, resistance_recovery_low), title="Break Above Recovery Zone", message="Price broke above recovery zone")
alertcondition(ta.crossunder(close, immediate_support), title="Break Below Immediate Support", message="Price broke below immediate support")
alertcondition(ta.crossunder(close, major_support_1), title="Break Below Major Support", message="Price broke below major support - Bearish continuation likely")

// Background color highlighting for key consolidation zones
in_consolidation_zone = close >= immediate_support and close <= resistance_recovery_low
bgcolor(in_consolidation_zone ? color.new(color.blue, 95) : na, title="Consolidation Zone")

// Highlight when price is between key Fibonacci levels
between_382_618 = close >= fib_382 and close <= fib_618
bgcolor(between_382_618 ? color.new(color.yellow, 97) : na, title="Key Fib Zone (38.2% - 61.8%)")

// Critical level alerts
above_618_fib = close > fib_618
below_382_fib = close < fib_382
bgcolor(above_618_fib ? color.new(color.green, 97) : na, title="Above 61.8% - Bullish")
bgcolor(below_382_fib ? color.new(color.red, 97) : na, title="Below 38.2% - Bearish")`;

// Removed hard-coded mock values - system now requires real extracted data